import React, { FC, useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

import Button from '../../../../../Button';
import Note from '../../../../Notes/Note';
import TicketLogIconButton from '../../../../Tickets/TicketLogIconButton';
import TimeFrom from '../../../../Common/TimeFrom';

import { Pin as PinIcon, Flag as FlagIcon, ArrowDown } from '@icons/common';

import { LOG_RELATIONS } from '../../../../../../constants';
import { TICKET_LOG_TYPES as TYPES } from '../../../../../../consts/logs';

import ITicketLog from '../../interfaces/ITicketLog';
import { pluralize } from '@utils/strings';
import { ValueOf } from '@utils/tsUtils';

const UPDATE_INTERVAL = 1 * 1000;

interface Props extends ITicketLog {
  className?: string;
  onClickIcon: (
    id: string,
    type: string,
    isBot: boolean,
    ticketId: string
  ) => void;
  onClickOutside: (onClickOutside: number, preventScroll: boolean) => void;
  onPin(params: {
    id: string;
    isPinned: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onFlag(params: {
    id: string;
    isFlagged: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onEdit(params: {
    id: string;
    value: string;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onExpiredEditable(): void;
  toggleTicketLogsGroup(params: { id: string }): void;
  onDeleteAttachment(params: {
    attachId: string;
    noteId: string;
    relateTo: ValueOf<typeof LOG_RELATIONS>;
  }): void;
}

interface IconProps {
  className?: string;
  avatarUrl: string;
  type: (typeof TYPES)[keyof typeof TYPES];
  value: string;
  onClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  [index: string]: unknown;
}

const TicketLog: FC<Props> = React.memo(
  ({
    avatarUrl,
    className,
    id,
    message,
    name,
    onClickIcon,
    onPin,
    onFlag,
    onEdit,
    toggleTicketLogsGroup,
    onDeleteAttachment,
    isPinned,
    time,
    type,
    value,
    valueText,
    editable,
    editableExpiresAt,
    grouped,
    groupCount,
    groupId,
    isExpanded,
    attachments,
  }) => {
    const [isHovered, setHovered] = useState(false);
    const toggleHover = hoverState => setHovered(hoverState);

    const [isEditing, setEditing] = useState(false);
    const toggleEditing = () => setEditing(!isEditing);

    const isNotExpiredEditable = () =>
      Boolean(
        editable &&
          (editableExpiresAt ? editableExpiresAt * 1000 >= Date.now() : true)
      );
    const [isEditable, setEditable] = useState(isNotExpiredEditable);

    const isFlagged = TYPES.TASK === type;

    const handlePin = useCallback(() => {
      onPin({
        id,
        isPinned: !isPinned,
        relateTo: LOG_RELATIONS.TICKET,
      });
    }, [onPin]);

    const handleFlag = useCallback(() => {
      onFlag({
        id,
        isFlagged: !isFlagged,
        relateTo: LOG_RELATIONS.TICKET,
      });
    }, [onFlag]);

    const handleEdit = useCallback(
      ({ value: changedValue }) => {
        onEdit({
          id,
          value: changedValue,
          relateTo: LOG_RELATIONS.TICKET,
        });
      },
      [onEdit]
    );

    const handleDeleteAttachment = (params: {
      attachId: string;
      noteId: string;
    }) => {
      onDeleteAttachment({ ...params, relateTo: LOG_RELATIONS.TICKET });
    };

    useEffect(() => {
      const interval = setInterval(() => {
        if (!isEditable) return;

        setEditable(isNotExpiredEditable());
      }, UPDATE_INTERVAL);
      return () => clearInterval(interval);
    }, [isEditable, editableExpiresAt]);

    let Content;

    if (grouped && groupId)
      return (
        <StyledGroup className={className} key={id}>
          <StyledHighlight>{name}</StyledHighlight>
          <div>
            {`совершил(а) ${groupCount} ${pluralize(groupCount, [
              'действие',
              'действия',
              'действий',
            ])}`}
          </div>
          <StyledExpandButton
            onClick={() => toggleTicketLogsGroup({ id: groupId })}
            $isExpanded={isExpanded}
          />
        </StyledGroup>
      );

    switch (type) {
      case TYPES.USER_CREATION_ACTION:
        Content = valueText;
        break;

      case TYPES.TASK:
      case TYPES.NOTE:
        Content = (
          <StyledHead>
            <StyledNoteContainer>
              <Note
                id={id}
                onToggleEdit={toggleEditing}
                onEdit={handleEdit}
                readOnly={!isEditable}
                value={valueText}
                attachments={attachments}
                onDeleteAttachment={handleDeleteAttachment}
              />
            </StyledNoteContainer>
            {!isEditing && (
              <>
                <StyledFlagButton
                  functional
                  title={
                    isFlagged ? 'Отметить как заметку' : 'Отметить как задачу'
                  }
                  onClick={handleFlag}
                  visible={isHovered}
                  active={isFlagged}
                >
                  <FlagIcon height={15} />
                </StyledFlagButton>
                <StyledPinButton
                  functional
                  title={isPinned ? 'Открепить' : 'Закрепить'}
                  onClick={handlePin}
                  visible={isHovered}
                  active={isPinned}
                >
                  <PinIcon height={15} />
                </StyledPinButton>
              </>
            )}
          </StyledHead>
        );
        break;

      case TYPES.USER_RATING:
        Content = 'NPS запрошен';
        break;

      default:
        Content = valueText;
        break;
    }

    return (
      <StyledTicketLog
        className={className}
        key={id}
        type={type}
        onMouseEnter={() => toggleHover(true)}
        onMouseLeave={() => toggleHover(false)}
        isEditing={isEditing}
      >
        <StyledTimeline>
          <StyledIcon
            avatarUrl={avatarUrl}
            value={value}
            type={type}
            onClick={onClickIcon as any}
          />
          <StyledTimelineLine />
        </StyledTimeline>
        <StyledContent>
          {Content}
          <StyledAction>
            <StyledHighlight>{name}</StyledHighlight>
            {message}
          </StyledAction>
          <StyledTimeFrom time={time} full extend />
        </StyledContent>
      </StyledTicketLog>
    );
  }
);

const StyledExpandButton = styled(ArrowDown)<{ $isExpanded?: boolean }>`
  width: 18px;
  cursor: pointer;
  margin: 0 5px 0 auto;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    css`
      transform: rotate(180deg);
    `}

  path {
    fill: #007aff;
  }
`;

const StyledHead = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledTimeline = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 7px;
  position: relative;
  width: 16px;
`;

const StyledTimelineLine = styled.div`
  background-color: #c2c5cc;
  height: 100%;
  margin: 0 auto;
  width: 1px;
`;

const StyledIcon = styled(TicketLogIconButton)<IconProps>`
  background-color: #e6e6e6;
  box-sizing: content-box;
  z-index: 2;
  margin-top: 1px;
  margin-bottom: 5px;
`;

const StyledPinButton = styled(Button)`
  opacity: 0;
  outline: none;
  path {
    stroke: #919499;
  }

  ${({ visible }) =>
    visible &&
    `
    opacity: 1;
  `}

  :hover {
    path {
      stroke: #007aff;
    }
  }

  ${({ active }) =>
    active &&
    `
    opacity: 1;
    path { stroke: #17181A; }
  `}
`;

const StyledFlagButton = styled(Button)`
  outline: none;
  margin-right: 10px;
  opacity: 0;
  path {
    stroke: #919499;
  }

  ${({ visible }) =>
    visible &&
    `
    opacity: 1;
  `}

  :hover {
    path {
      stroke: #007aff;
      fill: #007aff;
    }
  }

  ${({ active }) =>
    active &&
    `
    opacity: 1;
    path {
      stroke: #D0021B;
      fill: #D0021B;
    }
  `}
`;

const StyledContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const StyledHighlight = styled.span`
  color: #007aff;
  margin-right: 3px;
  font-weight: normal;
`;

const StyledAction = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  color: #919499;
`;

const StyledGroup = styled(StyledAction)`
  padding: 4px 0;
`;

const StyledTitle = styled.div`
  flex: 1;
`;

const StyledTimeFrom = styled(TimeFrom)`
  color: #919499;
`;

const StyledTicketLog = styled.div<{
  [index: string]: unknown;
}>`
  background-color: #e6e6e6;
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  padding: 5px 10px;
  position: relative;
  width: 100%;
  color: #4d4d4d;

  ${({ type, isEditing }) =>
    (type === TYPES.NOTE || type === TYPES.TASK) &&
    `
    z-index: ${isEditing ? 5 : 1};
    background: ${type === TYPES.TASK ? '#F9EAEC' : '#FFFBE5'};
    border-top: 1px solid #e6e6e6;
    box-shadow: 0px 2px 2px rgb(230 230 230);

    ${StyledAction} { color: #4D4D4D; }
    ${StyledIcon} { background: ${
      type === TYPES.TASK ? '#F9EAEC' : '#FFFBE5'
    }; }
    ${StyledTimelineLine} { background: #C8C7BF; }
    ${StyledTimeFrom} { color: #4D4D4D; }
  `}
`;

const StyledNoteContainer = styled.div`
  margin-bottom: 5px;
  flex: 1;
  max-width: 88%;
`;

export default TicketLog;
